import { Building2, LogOut, UserRound } from "lucide-react";
import { useCallback, useState } from "react";
import { ApiError } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { formatDate } from "@/lib/format";

/**
 * UI-13's Account section: who is signed in, which organization that puts them in, and
 * the way out.
 *
 * FOUNDATION is explicit that the app shows *the* organization the signed-in user belongs
 * to, and that switching, inviting and managing members are deferred until there is a
 * second user (DECISIONS.md 2026-08-23). So this says which organization, and does not
 * offer to change which one you are in.
 *
 * What it does offer is UI-18: the general information *about* that organization — its
 * name, what sort of organization it is, and what it does. That is not decoration on an
 * identity card. It is the only place the system is ever told who "we" is, and six
 * pipeline prompts are handed it: an insight reading "we will migrate Acme before Friday"
 * is unusable a year later for exactly the reason one reading "the system will be
 * migrated" is, and the entity glossary cannot close that gap, because an organization is
 * not one of its own entities.
 *
 * Signing out is handed up rather than done here: the Supabase client lives at the root
 * of the app, and a second one made in a settings page would sign out of a session this
 * one does not own.
 */
export function AccountSettings({ summary }: { summary: string }) {
  const { me, signOut } = useApp();

  const name = me.user.name ?? me.user.username ?? me.user.email ?? "Signed in";

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Account
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      <section className="flex items-center gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-6)] backdrop-blur-[var(--blur-sm)]">
        {/* Initials on a brand colour, never a photo — the handoff is explicit that
            there is no photo upload anywhere in this app. */}
        <span
          aria-hidden
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-full)] text-[length:var(--text-md)] font-bold text-[var(--text-inverse)]"
          style={{ background: "var(--vl-virtual)" }}
        >
          {initialsOf(name)}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[length:var(--text-lg)] font-semibold text-[var(--text-primary)]">
            {name}
          </p>
          {me.user.email && (
            <p className="truncate text-[length:var(--text-sm)] text-[var(--text-secondary)]">
              {me.user.email}
            </p>
          )}
        </div>

        <UserRound size={18} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />
      </section>

      <OrganizationCard />

      {signOut && (
        <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
          <div className="flex items-center justify-between gap-[var(--space-4)]">
            <div className="min-w-0">
              <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
                Sign out
              </p>
              <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                Ends this browser's session. The worker keeps running — it has a credential of its
                own.
              </p>
            </div>

            <button
              type="button"
              onClick={signOut}
              className="flex shrink-0 items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]"
            >
              <LogOut size={14} strokeWidth={2} />
              Sign out
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * UI-18 — the organization, and the general information about it.
 *
 * One Save for the whole form rather than per-field autosave: the three fields are read
 * together by every prompt that gets them, and a half-typed description reaching the next
 * extraction pass is worse than a button. Blank clears, which is why the write is a whole
 * one — a description somebody no longer wants has to be able to go away, the gap the
 * user-settings patch documents and cannot close.
 *
 * Only an owner or an admin may write it (the Server refuses the rest with a 403), so a
 * plain member is shown the same three fields read-only rather than a form that fails on
 * submit.
 */
function OrganizationCard() {
  const { organization, updateOrganizationProfile } = useApp();

  const [name, setName] = useState(organization?.name ?? "");
  const [kind, setKind] = useState(organization?.kind ?? "");
  const [description, setDescription] = useState(organization?.description ?? "");
  const [aliases, setAliases] = useState(joinAliases(organization?.aliases));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const mayEdit = organization?.role === "owner" || organization?.role === "admin";

  const dirty =
    name !== (organization?.name ?? "")
    || kind !== (organization?.kind ?? "")
    || description !== (organization?.description ?? "")
    || aliases !== joinAliases(organization?.aliases);

  const save = useCallback(async () => {
    setSaving(true);
    setProblem(null);
    setSaved(false);

    try {
      await updateOrganizationProfile({
        name: name.trim(),
        kind: kind.trim() === "" ? null : kind.trim(),
        description: description.trim() === "" ? null : description.trim(),
        aliases: splitAliases(aliases),
      });
      setSaved(true);
    } catch (cause: unknown) {
      setProblem(
        cause instanceof ApiError
          ? `The Server refused the change (${cause.status}): ${cause.message}`
          : "The Server could not be reached.",
      );
    } finally {
      setSaving(false);
    }
  }, [aliases, description, kind, name, updateOrganizationProfile]);

  if (!organization) {
    return (
      <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
        <div className="flex items-center gap-[var(--space-3)]">
          <Building2 size={16} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />
          <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
            No organization
          </p>
        </div>
        <p className="mt-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
          Nothing has been created yet.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-[var(--radius-xl)] border border-[var(--border-subtle)] p-[var(--space-6)]">
      <div className="flex items-center gap-[var(--space-3)]">
        <Building2 size={16} strokeWidth={2} className="shrink-0 text-[var(--text-secondary)]" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">
            Your organization
          </p>
          <p className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
            {`You are ${article(organization.role)} ${organization.role} here, since ${formatDate(organization.createdAt)}.`}
          </p>
        </div>
      </div>

      {mayEdit ? (
        <div className="mt-[var(--space-6)] flex flex-col gap-[var(--space-4)]">
          <Field label="Name">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
              placeholder="e.g. Van Dijk Logistics B.V."
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          <Field
            label="Also called"
            hint="Other names and spellings, separated by commas. The transcriber is told to listen for these, so your own name stops coming back as something else."
          >
            <input
              value={aliases}
              onChange={(event) => setAliases(event.target.value)}
              placeholder="e.g. CM, CM.com, seem dot com"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          <Field label="Type of organization">
            <input
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              maxLength={80}
              placeholder="e.g. IT consultancy, accountancy firm, logistics operator"
              className="h-9 w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 text-[length:var(--text-sm)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          <Field label="What it does">
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="A couple of sentences: what you do, for whom, and what a newcomer would need to make sense of a meeting here…"
              className="w-full resize-y rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-3 py-2 text-[length:var(--text-sm)] leading-relaxed text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
            />
          </Field>

          {problem && (
            <p role="alert" className="text-[length:var(--text-xs)] text-[var(--warning)]">
              {problem}
            </p>
          )}

          <div className="flex items-center gap-[var(--space-3)]">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !dirty || name.trim() === ""}
              className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[8px] text-[length:var(--text-sm)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>

            {saved && !dirty && (
              <span role="status" className="text-[length:var(--text-xs)] text-[var(--text-secondary)]">
                Saved. The next pipeline run reads it.
              </span>
            )}
          </div>
        </div>
      ) : (
        <dl className="mt-[var(--space-6)] flex flex-col gap-[var(--space-3)]">
          <ReadOnly label="Name" value={organization.name} />
          <ReadOnly label="Also called" value={joinAliases(organization.aliases) || null} />
          <ReadOnly label="Type of organization" value={organization.kind} />
          <ReadOnly label="What it does" value={organization.description} />
          <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
            Only an owner or an admin can change this.
          </p>
        </dl>
      )}

      <p className="mt-[var(--space-4)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        This is the only place the system is told who "we" is. Transcript correction, insight
        extraction, world-view synthesis, relation derivation, commitment tracking and person
        dossiers are all given it — so "we agreed to migrate Acme" can be written down naming
        who agreed, and so what counts as a durable business fact is judged in your trade
        rather than in general. Every source, insight and belief belongs to this organization
        and can never leak into another.
      </p>

      <p className="mt-[var(--space-3)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        The names go somewhere else: entities are the things you talk about, aliases are what
        you call them, and the transcriber is told the ones you say most. Your own name and the
        names above are always among them. Everything else it listens for comes from{" "}
        <span className="text-[var(--text-secondary)]">Entities</span>, in the order those are
        actually heard in.
      </p>
    </section>
  );
}

/**
 * One labelled control, with the hint deliberately *outside* the `label`.
 *
 * A hint inside it becomes part of the field's accessible name — so the control announces
 * itself as "Also called Other names and spellings, separated by commas…" to a screen
 * reader, and `getByLabelText("Also called")` stops finding it. Both are the same bug seen
 * from two sides, and the second is what caught it.
 */
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex flex-col gap-1.5">
        <span className="text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
          {label}
        </span>
        {children}
      </label>
      {hint && (
        <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{hint}</span>
      )}
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[length:var(--text-xs)] font-semibold text-[var(--text-secondary)]">
        {label}
      </dt>
      <dd className="text-[length:var(--text-sm)] text-[var(--text-primary)]">
        {value ?? <span className="text-[var(--text-muted)]">Not set</span>}
      </dd>
    </div>
  );
}

/**
 * The alias list as one line of text, and back.
 *
 * A comma-separated field rather than the chip control the entity detail uses, and that is a
 * deliberate difference rather than an inconsistency. An entity's aliases are a list somebody
 * curates over months, added one at a time from several screens; an organization has two or
 * three names, typed once, in a card that already saves everything at once behind one button.
 * A chip control here would be a second interaction model to learn for a field most people
 * fill in on the day they sign up and never open again.
 */
function joinAliases(aliases: string[] | undefined): string {
  return (aliases ?? []).join(", ");
}

function splitAliases(text: string): string[] {
  const kept: string[] = [];
  const seen = new Set<string>();

  for (const raw of text.split(",")) {
    const alias = raw.trim();
    // Case-folded for the duplicate check and kept as typed, the same rule the Server
    // applies — so a list that came back from a save reads exactly as it was written.
    if (alias !== "" && !seen.has(alias.toLowerCase())) {
      seen.add(alias.toLowerCase());
      kept.push(alias);
    }
  }

  return kept;
}

function initialsOf(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]).join("");

  return (letters || name.slice(0, 2)).toUpperCase();
}

function article(role: string): string {
  return /^[aeiou]/i.test(role) ? "an" : "a";
}
